import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { documentService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import { NodeFileTypeIcon } from '@/components/nodes/NodeFileTypeIcon'
import type { Document } from '@/types'
import { Edit, ExternalLink, Download, Trash2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  PageContainer,
  PageHeader,
  LoadingState,
  HistoryCard,
  AssociationsCard,
} from '@/components/layout'
import { DetailPageLayout } from '@/components/layout/DetailPageLayout'
import { NodeDate } from '@/components/nodes/NodeDate'

export default function DocumentDetailPage() {
  const { documentId } = useParams<{ documentId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [document, setDocument] = useState<Document | null>(null)
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(false)

  const loadDocument = useCallback(async () => {
    if (!documentId) return

    try {
      setLoading(true)
      const documentData = await documentService.getById(documentId)
      if (documentData) {
        setDocument(documentData)
      } else {
        toast.error('Document not found')
        navigate('/documents')
      }
    } catch (error) {
      console.error('Failed to load document:', error)
      toast.error('Failed to load document')
      navigate('/documents')
    } finally {
      setLoading(false)
    }
  }, [documentId, navigate])

  useEffect(() => {
    loadDocument()
  }, [loadDocument])

  const handleDelete = async () => {
    if (!document || !documentId) return

    if (
      !confirm(`Are you sure you want to delete "${document.name}"? This action cannot be undone.`)
    ) {
      return
    }

    setDeleting(true)
    try {
      await documentService.delete(documentId)
      toast.success('Document deleted successfully')
      navigate('/documents')
    } catch (error) {
      console.error('Failed to delete document:', error)
      toast.error('Failed to delete document')
      setDeleting(false)
    }
  }

  const canEdit = () => {
    if (!user || !document) return false
    // User can edit if they're the creator or a member of the workspace
    return document.creator.id === user.id || document.workspace.users.some((u) => u.id === user.id)
  }

  if (loading) {
    return (
      <PageContainer maxWidth="full">
        <LoadingState text="Loading document..." />
      </PageContainer>
    )
  }

  if (!document) {
    return null
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title={document.name}
        metadata={
          <>
            <NodeDate dueDate={document.createdAt} label="Uploaded on" showOverdue={false} />
            <NodeFileTypeIcon type={document.type} />
          </>
        }
        customActions={
          <div className="flex flex-col sm:flex-row gap-2">
            {document.url && (
              <>
                <Button variant="outline" asChild>
                  <a href={document.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open
                  </a>
                </Button>
                <Button variant="outline" asChild>
                  <a href={document.url} download>
                    <Download className="mr-2 h-4 w-4" />
                    Download
                  </a>
                </Button>
              </>
            )}
            {canEdit() && (
              <>
                <Button onClick={() => navigate(`/documents/${documentId}/edit`)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Document
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="text-destructive hover:text-destructive"
                >
                  {deleting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  Delete
                </Button>
              </>
            )}
          </div>
        }
      />

      <DetailPageLayout
        layout="default"
        className="mt-6"
        sidebar={
          <>
            {/* Associated Users, Workspace, Projects and Tasks */}
            <AssociationsCard
              title="Associations"
              associations={[
                { type: 'users' as const, options: { label: 'Shared With' } },
                { type: 'workspace' as const },
                ...(document.projects.length > 0 ? [{ type: 'projects' as const }] : []),
                ...(document.tasks.length > 0 ? [{ type: 'tasks' as const }] : []),
              ]}
              data={{
                users: document.users,
                workspace: document.workspace,
                projects: document.projects,
                tasks: document.tasks,
              }}
            />

            {/* Document History */}
            <HistoryCard
              creator={document.creator}
              createdAt={document.createdAt}
              updatedAt={document.updatedAt}
              title="History"
            />
          </>
        }
      >
        {/* Description */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground whitespace-pre-wrap">{document.description}</p>
          </CardContent>
        </Card>
      </DetailPageLayout>
    </PageContainer>
  )
}
